/**
 * Provinces of Iran with their capital and main cities, as Persian display names.
 * Reference data for address pickers; the API stores the chosen names as text.
 */
export interface IranProvince {
  readonly name: string;
  readonly cities: readonly string[];
}

export const IRAN_PROVINCES: readonly IranProvince[] = [
  {
    name: 'آذربایجان شرقی',
    cities: ['تبریز', 'مراغه', 'مرند', 'میانه', 'اهر', 'شبستر', 'بناب', 'سراب'],
  },
  {
    name: 'آذربایجان غربی',
    cities: ['ارومیه', 'خوی', 'مهاباد', 'میاندوآب', 'بوکان', 'سلماس', 'پیرانشهر', 'نقده'],
  },
  { name: 'اردبیل', cities: ['اردبیل', 'پارس‌آباد', 'مشگین‌شهر', 'خلخال', 'گرمی', 'بیله‌سوار'] },
  {
    name: 'اصفهان',
    cities: [
      'اصفهان',
      'کاشان',
      'نجف‌آباد',
      'خمینی‌شهر',
      'شاهین‌شهر',
      'زرین‌شهر',
      'فلاورجان',
      'مبارکه',
      'گلپایگان',
      'نایین',
    ],
  },
  { name: 'البرز', cities: ['کرج', 'فردیس', 'نظرآباد', 'هشتگرد', 'محمدشهر', 'ماهدشت', 'اشتهارد'] },
  { name: 'ایلام', cities: ['ایلام', 'دهلران', 'ایوان', 'آبدانان', 'مهران', 'دره‌شهر'] },
  {
    name: 'بوشهر',
    cities: ['بوشهر', 'برازجان', 'کنگان', 'جم', 'گناوه', 'دیر', 'خورموج', 'عسلویه'],
  },
  {
    name: 'تهران',
    cities: [
      'تهران',
      'شهریار',
      'اسلامشهر',
      'ری',
      'پاکدشت',
      'ورامین',
      'قدس',
      'ملارد',
      'بهارستان',
      'پردیس',
      'رباط‌کریم',
      'دماوند',
      'لواسان',
    ],
  },
  {
    name: 'چهارمحال و بختیاری',
    cities: ['شهرکرد', 'بروجن', 'فارسان', 'لردگان', 'سامان', 'فرخ‌شهر'],
  },
  { name: 'خراسان جنوبی', cities: ['بیرجند', 'قائن', 'طبس', 'فردوس', 'نهبندان', 'سرایان'] },
  {
    name: 'خراسان رضوی',
    cities: [
      'مشهد',
      'نیشابور',
      'سبزوار',
      'تربت‌حیدریه',
      'کاشمر',
      'قوچان',
      'تربت‌جام',
      'تایباد',
      'گناباد',
      'چناران',
    ],
  },
  { name: 'خراسان شمالی', cities: ['بجنورد', 'شیروان', 'اسفراین', 'آشخانه', 'جاجرم'] },
  {
    name: 'خوزستان',
    cities: [
      'اهواز',
      'آبادان',
      'خرمشهر',
      'دزفول',
      'ماهشهر',
      'بهبهان',
      'ایذه',
      'شوشتر',
      'اندیمشک',
      'شوش',
      'رامهرمز',
      'مسجدسلیمان',
    ],
  },
  { name: 'زنجان', cities: ['زنجان', 'ابهر', 'خرمدره', 'قیدار', 'طارم'] },
  { name: 'سمنان', cities: ['سمنان', 'شاهرود', 'دامغان', 'گرمسار', 'مهدی‌شهر', 'ایوانکی'] },
  {
    name: 'سیستان و بلوچستان',
    cities: ['زاهدان', 'چابهار', 'زابل', 'ایرانشهر', 'سراوان', 'خاش', 'کنارک'],
  },
  {
    name: 'فارس',
    cities: [
      'شیراز',
      'مرودشت',
      'جهرم',
      'فسا',
      'کازرون',
      'لار',
      'داراب',
      'فیروزآباد',
      'آباده',
      'اقلید',
      'نورآباد',
    ],
  },
  { name: 'قزوین', cities: ['قزوین', 'البرز', 'تاکستان', 'بوئین‌زهرا', 'آبیک', 'محمدیه'] },
  { name: 'قم', cities: ['قم'] },
  { name: 'کردستان', cities: ['سنندج', 'سقز', 'بانه', 'مریوان', 'قروه', 'بیجار', 'کامیاران'] },
  {
    name: 'کرمان',
    cities: ['کرمان', 'رفسنجان', 'سیرجان', 'جیرفت', 'بم', 'زرند', 'کهنوج', 'بردسیر'],
  },
  {
    name: 'کرمانشاه',
    cities: ['کرمانشاه', 'اسلام‌آباد غرب', 'پاوه', 'سنقر', 'کنگاور', 'هرسین', 'صحنه', 'جوانرود'],
  },
  { name: 'کهگیلویه و بویراحمد', cities: ['یاسوج', 'دهدشت', 'گچساران', 'دوگنبدان', 'لیکک'] },
  {
    name: 'گلستان',
    cities: ['گرگان', 'گنبد کاووس', 'علی‌آباد کتول', 'آق‌قلا', 'بندر ترکمن', 'مینودشت', 'کردکوی'],
  },
  {
    name: 'گیلان',
    cities: ['رشت', 'انزلی', 'لاهیجان', 'لنگرود', 'تالش', 'آستارا', 'رودسر', 'صومعه‌سرا', 'فومن'],
  },
  {
    name: 'لرستان',
    cities: ['خرم‌آباد', 'بروجرد', 'دورود', 'الیگودرز', 'کوهدشت', 'ازنا', 'نورآباد'],
  },
  {
    name: 'مازندران',
    cities: [
      'ساری',
      'بابل',
      'آمل',
      'قائمشهر',
      'بابلسر',
      'نوشهر',
      'چالوس',
      'تنکابن',
      'رامسر',
      'محمودآباد',
    ],
  },
  { name: 'مرکزی', cities: ['اراک', 'ساوه', 'خمین', 'محلات', 'دلیجان', 'شازند', 'آشتیان'] },
  {
    name: 'هرمزگان',
    cities: ['بندرعباس', 'میناب', 'قشم', 'کیش', 'بندر لنگه', 'جاسک', 'حاجی‌آباد', 'رودان'],
  },
  {
    name: 'همدان',
    cities: ['همدان', 'ملایر', 'نهاوند', 'تویسرکان', 'اسدآباد', 'کبودرآهنگ', 'رزن'],
  },
  { name: 'یزد', cities: ['یزد', 'میبد', 'اردکان', 'ابرکوه', 'بافق', 'تفت', 'مهریز'] },
];

export function citiesOfProvince(province: string): readonly string[] {
  return IRAN_PROVINCES.find((item) => item.name === province)?.cities ?? [];
}
